import styled, { css } from "styled-components";

const SURFACE_HOVER = "#f8fafc";
const TEXT_PRIMARY = "#0f172a";
const TEXT_SECONDARY = "#475569";

type ItemButtonProps = {
  $depth: number;
  $hasChildren: boolean;
  $highlightColor: string;
  $applyHighlight: boolean;
};

type HeaderProps = {
  $hasChildren: boolean;
};

type ItemNameProps = {
  $highlight: boolean;
  $isLeaf: boolean;
  $depth: number;
};

type ItemDetailsProps = {
  $isLeaf: boolean;
  $highlight: boolean;
};

type ChevronProps = {
  $isOpen: boolean;
};

export const DropdownContainer = styled.div`
  width: 100%;
  border-radius: 0.5rem;
  background-color: #ffffff;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1),
    0 4px 6px -2px rgba(0, 0, 0, 0.05);
  max-height: var(--dropdown-max-height, 70vh);
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
`;

export const DropdownList = styled.div`
  display: flex;
  flex-direction: column;
`;

export const DropdownItemButton = styled.button<ItemButtonProps>`
  width: 100%;
  display: flex;
  align-items: stretch;
  justify-content: flex-start;
  padding: ${({ $depth }) => ($depth === 1 ? "16px" : "8px 16px")};
  padding-left: ${({ $depth }) => ($depth >= 3 ? 24 : 16)}px;
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  transition: background-color 0.25s ease, color 0.25s ease;

  &:focus-visible {
    outline: 2px solid ${({ $highlightColor }) => $highlightColor};
    outline-offset: -2px;
  }

  &:hover {
    background-color: ${SURFACE_HOVER};
  }

  ${({ $applyHighlight, $highlightColor }) =>
    $applyHighlight &&
    css`
      background-color: ${$highlightColor};
      color: #ffffff;

      &:hover {
        background-color: ${$highlightColor};
      }
    `}

  ${({ $hasChildren }) =>
    !$hasChildren &&
    css`
      font-weight: 600;
    `}
`;

export const DropdownItemContent = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
`;

export const DropdownItemHeader = styled.span<HeaderProps>`
  display: flex;
  align-items: center;
  width: 100%;
  justify-content: ${({ $hasChildren }) =>
    $hasChildren ? "space-between" : "flex-start"};
`;

export const DropdownItemName = styled.span<ItemNameProps>`
  font-size: 1rem;
  font-weight: 600;
  color: ${TEXT_PRIMARY};

  ${({ $isLeaf, $depth }) =>
    $isLeaf &&
    $depth >= 2 &&
    css`
      color: ${TEXT_SECONDARY};
    `}

  ${({ $highlight }) =>
    $highlight &&
    css`
      color: #ffffff;
    `}
`;

export const DropdownItemDetails = styled.span<ItemDetailsProps>`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  color: ${TEXT_SECONDARY};
  width: ${({ $isLeaf }) => ($isLeaf ? "100%" : "auto")};
  justify-content: ${({ $isLeaf }) =>
    $isLeaf ? "space-between" : "flex-start"};

  ${({ $highlight }) =>
    $highlight &&
    css`
      color: rgba(255, 255, 255, 0.85);
    `}
`;

export const DropdownItemPhone = styled.span`
  white-space: nowrap;
  font-weight: 400;
`;

export const DropdownItemChevron = styled.span<ChevronProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  transition: transform 0.25s ease;

  ${({ $isOpen }) =>
    $isOpen &&
    css`
      transform: rotate(180deg);
    `}
`;

export const DropdownItemChildren = styled.div`
  display: flex;
  flex-direction: column;

  & > :last-child {
    padding-bottom: 8px;
  }
`;
